import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { subscribeToPush, isPushSubscribed } from "@/lib/push";

export const NotificationBell = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = () => api.get("/notifications").then((r) => {
    setData(r.data);
    if ("setAppBadge" in navigator) {
      try {
        if (r.data.unread > 0) navigator.setAppBadge(r.data.unread);
        else navigator.clearAppBadge();
      } catch (e) {}
    }
  }).catch(() => {});

  useEffect(() => {
    load();
    isPushSubscribed().then(setPushOn);
    const t = setInterval(load, 25000);
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => { clearInterval(t); document.removeEventListener("mousedown", close); };
  }, []);

  const enablePush = async () => {
    try {
      await subscribeToPush();
      setPushOn(true);
      toast.success("Notificaciones push activadas en este dispositivo");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const markAll = async () => {
    await api.patch("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen(!open); if (!open && data.unread > 0) markAll(); }}
        className="relative p-2 rounded-sm hover:bg-neutral-800 text-neutral-300 hover:text-white transition-colors" data-testid="notification-bell">
        {data.unread > 0 ? <BellRing className="w-5 h-5 text-[#F2B90D]" /> : <Bell className="w-5 h-5" />}
        {data.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-[#F2B90D] text-black text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="notification-count">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-[#171717] border border-neutral-800 rounded-md shadow-2xl z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-800">
            <span className="font-heading font-bold uppercase text-xs tracking-wider">Notificaciones</span>
            <button onClick={markAll} className="text-neutral-400 hover:text-white" title="Marcar todas como leídas" data-testid="notification-mark-all">
              <CheckCheck className="w-4 h-4" />
            </button>
          </div>
          {!pushOn && (
            <button onClick={enablePush} className="w-full text-left px-4 py-2.5 bg-[#F2B90D]/10 text-[#F2B90D] text-xs font-medium hover:bg-[#F2B90D]/20 border-b border-neutral-800" data-testid="enable-push-btn">
              🔔 Activar notificaciones push en este dispositivo
            </button>
          )}
          <div className="max-h-80 overflow-y-auto">
            {data.items.length === 0 && <p className="text-neutral-500 text-xs p-4">Sin notificaciones</p>}
            {data.items.map((n) => (
              <button key={n.id} onClick={() => { setOpen(false); navigate(n.url || "/"); }}
                className={`w-full text-left px-4 py-3 border-b border-neutral-800 last:border-0 hover:bg-neutral-800 transition-colors ${!n.read ? "bg-[#F2B90D]/5" : ""}`}
                data-testid={`notification-item-${n.id}`}>
                <p className="text-xs font-bold text-white">{n.title}</p>
                <p className="text-xs text-neutral-400 mt-0.5 line-clamp-2">{n.body}</p>
                <p className="text-[10px] text-neutral-600 mt-1">{new Date(n.created_at).toLocaleString("es-AR")}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
